import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { CircleNotch, Eye, EyeSlash, Lock } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BG_IMAGE = "/login_building.jpg";
const REACH_LOGO = "/reach_logo.png";
const TFSI_FOURSQUARE = "/tfsi_foursquare.png";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [idleMsg, setIdleMsg] = useState(() => {
    const flag = sessionStorage.getItem("idle_logout");
    if (flag) sessionStorage.removeItem("idle_logout");
    return flag ? "You were signed out after 30 minutes of inactivity." : "";
  });
  const [loading, setLoading] = useState(false);

  const redirectTo = "/dashboard"; // all roles land on their Dashboard after sign-in

  // Redirect in an effect (not during render) to avoid the
  // "Cannot update a component while rendering" React warning.
  useEffect(() => {
    if (user) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, navigate, redirectTo]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) setError(res.error || "Unable to sign in");
    // on success, the effect above navigates once `user` is set
  };

  return (
    <div className="flex min-h-[100dvh] w-full bg-white">
      {/* Left visual panel — corporate building artwork (asset untouched) with REACH branding overlays */}
      <div className="relative hidden w-1/2 lg:block" data-testid="login-brand-panel">
        <img src={BG_IMAGE} alt="Total Facility Solutions corporate building" className="absolute inset-0 h-full w-full object-cover" />
        {/* Legibility gradient only — the building image itself is not modified */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#002248]/75 via-[#002248]/20 to-[#002248]/60" />
        <div className="absolute inset-0 flex flex-col p-12 text-white">
          <div className="flex flex-col items-center pt-4">
            <img src={REACH_LOGO} alt="REACH — connect. advance. grow." className="h-52 w-52 rounded-[2.5rem] shadow-xl" data-testid="login-reach-logo" />
            <p className="mt-8 text-sm font-semibold uppercase tracking-[0.3em] text-white/90" data-testid="login-platform-descriptor">
              Prospect &amp; Sales Management Platform
            </p>
          </div>
          <div className="mt-auto flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
            <div className="flex items-center gap-4" data-testid="login-tfsi-footer">
              <img src={TFSI_FOURSQUARE} alt="TFSI" className="h-11 w-11" />
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-white">
                  A Total Facility Solutions, Inc. Application
                </p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.3em] text-white/80">
                  Facility Forward.
                </p>
              </div>
            </div>
            {/* Batch I: developer/technology-division identification (right side) */}
            <div className="flex flex-col items-start" data-testid="login-tt-footer">
              <p className="text-[9px] font-medium tracking-[0.08em] text-white/70">developed and powered by</p>
              <img src="/total_technologies_logo.webp" alt="Total Technologies — A Total Facility Solutions, Inc division"
                className="mt-1 h-auto w-44 max-w-[40vw] xl:w-52" data-testid="login-tt-logo" />
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div
        className="flex w-full items-center justify-center px-6 py-4 lg:w-1/2 lg:py-0"
        style={{ paddingTop: "max(1rem, env(safe-area-inset-top))", paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        <div className="w-full max-w-sm">
          {/* Mobile header — REACH logo rendered on its natural transparent edges (no mask, no backing) */}
          <div className="-mt-2 mb-4 flex flex-col items-center gap-2 lg:hidden">
            <img src={REACH_LOGO} alt="REACH — connect. advance. grow." className="h-28 w-28 object-contain" data-testid="login-reach-logo-mobile" />
            <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#002248]">
              Prospect &amp; Sales Management Platform
            </p>
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#569A1F]">Sign in</p>
          <h2 className="mt-1 font-heading text-2xl font-black tracking-tight text-[#09090B] lg:mt-2 lg:text-3xl">Welcome back</h2>
          <p className="mt-1 text-sm text-[#6D6D70] lg:mt-2">Enter your credentials to access REACH.</p>

          <form onSubmit={submit} className="mt-5 space-y-4 lg:mt-8 lg:space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wide text-[#52525B]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@tfsi-us.com"
                className="rounded-sm"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wide text-[#52525B]">
                  Password
                </Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-[#569A1F] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-sm pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6D6D70] hover:text-[#032852]"
                  data-testid="toggle-password"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {idleMsg && !error && (
              <p className="rounded-sm border border-[#B45309]/40 bg-[#F59E0B]/10 px-3 py-2 text-sm text-[#B45309]" data-testid="idle-logout-message">
                {idleMsg}
              </p>
            )}
            {error && (
              <p className="rounded-sm border border-[#EF4444]/30 bg-[#EF4444]/5 px-3 py-2 text-sm text-[#EF4444]" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-[#569A1F] py-5 font-semibold text-white transition-colors hover:bg-[#457d18]"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <CircleNotch size={18} className="animate-spin" />
              ) : (
                <>
                  <Lock size={16} /> Sign in
                </>
              )}
            </Button>
          </form>

          <p className="mt-5 text-center text-xs text-[#6D6D70] lg:mt-8">
            Accounts are provisioned by your administrator.
          </p>

          {/* Batch I (mobile): Total Technologies division mark — white-background variant, transparent edges */}
          <div className="mt-4 flex justify-center lg:hidden" data-testid="login-tt-mobile">
            <img
              src="/total_technologies_logo_white_bg.webp"
              alt="Total Technologies — A Total Facility Solutions, Inc division"
              className="h-auto w-44 max-w-[65%] object-contain"
              data-testid="login-tt-logo-mobile"
            />
          </div>

          <div className="mt-5 border-t border-[#E4E4E7] pt-4 lg:mt-8" data-testid="login-proprietary-notice">
            <p className="text-center text-[11px] leading-relaxed text-[#6D6D70]">
              This system is proprietary to Total Facility Solutions, Inc. Access is restricted to
              authorized users. Unauthorized access, use, reproduction, or distribution is prohibited.
            </p>
            <p className="mt-2 text-center text-[11px] text-[#6D6D70]" data-testid="login-copyright">
              © 2026 Total Facility Solutions, Inc. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
